/**
 * dink-admin Material UI theme — the single source of truth that maps the
 * design handoff's Design Tokens (see `docs/design/design_handoff_dink_admin`
 * → README "Design Tokens") onto a real MUI theme. Adapted from the handoff's
 * `mui_conversion/theme.ts` reference kit.
 *
 * Color-mode foundation (DNKADM-0266) — ported from the member app's
 * (`dink-journix`) `appTheme.ts` pattern, adapted for dink-admin's FIXED brand
 * (no tenant-branding argument — the `COURT_GREEN`/`COURT_GREEN_DARK` trio is
 * constant in both modes):
 *  - `AdminTokens` is the semantic token namespace, exposed on every built
 *    theme as `theme.tokens` (module augmentation below).
 *  - `buildTokens(mode)` returns the per-mode token values. The SURFACE ladder
 *    (backgrounds/panels/borders/dividers/body text) is the DNKADM-0297
 *    low-chroma hue-155 OKLCH scale in both modes (superseding the 0266
 *    "light = pre-0266 literals" identity constraint for those tokens only);
 *    brand and semantic status values are unchanged.
 *  - `buildAdminTheme(mode)` wires the MUI palette + every component override
 *    from tokens, so both the palette-driven surfaces and the overrides below
 *    flip per mode.
 *  - `resolveTokens(theme)` reads `theme.tokens` with a safe fallback for a
 *    bare `createTheme()` (e.g. component tests that don't mount the app
 *    theme) — shared primitives read tokens through this, never
 *    `theme.tokens.*` directly.
 *
 * CONVENTION (enforced since DNKADM-0269 by the `no-restricted-syntax` color
 * guard in `eslint.config.js`): feature surfaces read `theme.tokens.*` via
 * `resolveTokens(useTheme())` — plus the mode-aware semantic maps
 * (`resolveMaps` in `./tokens`, domain pairs in `./maps/*`) and the
 * `inkAlpha(theme, α)` escape hatch — never raw hex/rgba literals. The one
 * remaining raw-literal dir is `src/features/reservations/calendar/**`,
 * temporarily lint-exempt until the calendar redesign (DNKADM-0271)
 * tokenizes it.
 *
 * Stack: `@mui/material` + `@emotion`. Mounted once app-wide via
 * `AdminThemeProvider` (ThemeModeProvider → `useMemo(buildAdminTheme)` →
 * `ThemeProvider` + `CssBaseline`) in `main.tsx`. The mode preference persists
 * under the `dink_admin_theme_mode` storage key (see
 * `src/context/ThemeModeContext.tsx`).
 *
 * The literal light color/map values live in `./tokens` so components can read
 * the status/tier/avatar maps the theme can't express per-value; this file
 * wires the palette, typography (incl. the reusable `mono` treatment), shape,
 * shadows, and component density onto MUI.
 */
import { createTheme, type Theme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import {
  COURT_GREEN,
  COURT_GREEN_DARK,
  GREEN_TINTS,
  MONO,
  PAGE_BG,
  RADII,
  SANS,
  SHADOWS,
  STATUS,
} from './tokens';

// ---- Typography augmentation: a reusable, typed `mono` treatment ----
// Numbers, IDs, credit balances and bucket keys render in JetBrains Mono. Adding
// it to the theme's typography variants makes it usable as
// <Typography variant="mono"> and via theme.typography.mono.
declare module '@mui/material/styles' {
  interface TypographyVariants {
    mono: CSSProperties;
  }
  interface TypographyVariantsOptions {
    mono?: CSSProperties;
  }
  interface Theme {
    tokens: AdminTokens;
  }
  interface ThemeOptions {
    tokens?: AdminTokens;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
  }
}

// ---- The semantic token namespace (DNKADM-0266) ----
// Non-MUI design tokens, exposed via `theme.tokens` so components read e.g.
// `theme.tokens.line` instead of magic hex/rgba. Light values are the exact
// pre-0266 literals; dark values are the dark palette.
export interface AdminTokens {
  /** Page background (body / `background.default`). */
  bg: string;
  /** Card/paper/AppBar surface (`background.paper`). */
  surface: string;
  /** Subtly tinted surface — table heads. */
  surface2: string;
  /** Raised inset surface — the nav's current-user chip. */
  raise: string;
  /** Standard divider / border (`palette.divider`). */
  line: string;
  /** Stronger control border (ToggleButton). */
  lineStrong: string;
  /** Soft hairline — logo lockup / user-chip separators. */
  lineSoft: string;
  /** Faintest row separator — table body cell borders. */
  lineFaint: string;
  /** Primary text (`text.primary`). */
  text: string;
  /** Secondary text (`text.secondary`). */
  text2: string;
  /** Disabled text (`text.disabled`). */
  text3: string;
  /** Subtle metadata text — the user chip's role label. */
  textSubtle: string;
  /** Muted label text — nav section labels. */
  textMuted: string;
  /** Ghost/decorative glyphs — the locked-nav padlocks. */
  textGhost: string;
  /** Brand green — FIXED across modes (`COURT_GREEN`). */
  brand: string;
  /** Link text — brand green in light mode, contrast-safe green in dark mode. */
  link: string;
  /** Brand hover/pressed — FIXED across modes (`COURT_GREEN_DARK`). */
  brandDark: string;
  /** Readable ink on a brand fill — FIXED across modes. */
  brandInk: string;
  /** Brand-tinted fill — active nav item background. */
  brandSoft: string;
  /** Stronger brand tint — active-nav hover / front-desk chip. */
  brandSoft2: string;
  /** Faint brand wash — nav item hover. */
  brandFaint: string;
  /** Brand-tinted border (reserved for per-surface conversions). */
  brandLine: string;
  success: string;
  successDark: string;
  /** Success alert/chip surface (light = `STATUS.active.bg`). */
  successSoft: string;
  /** Ink on `successSoft` (light = `STATUS.active.fg`). */
  successSoftInk: string;
  warn: string;
  warnDark: string;
  warnSoft: string;
  warnSoftInk: string;
  warnSoftLine: string;
  /** Warn-tinted count badge — the nav Confirmations chip. */
  warnBadgeBg: string;
  warnBadgeInk: string;
  /** Error foreground for text and icons on a neutral surface. */
  error: string;
  /** Error background for destructive controls whose foreground is `brandInk`. */
  errorFill: string;
  errorDark: string;
  errorSoft: string;
  errorSoftInk: string;
  errorSoftLine: string;
  /** Info accent (reference only — `palette.info` keeps MUI's per-mode default). */
  info: string;
  infoSoft: string;
  infoSoftInk: string;
  shadowCard: string;
  shadowDialog: string;
  shadowAppBar: string;
}

/**
 * Per-mode token values. The surface ladder in BOTH modes is the DNKADM-0297
 * OKLCH scale (hue 155, chroma ≤0.013, fine lightness steps — dark uses the
 * approved exploration's values, light is the derived mirror); brand and
 * semantic tints keep their pre-0297 literals. The brand trio is constant.
 */
export function buildTokens(mode: 'light' | 'dark'): AdminTokens {
  const brand = {
    brand: COURT_GREEN,
    brandDark: COURT_GREEN_DARK,
    brandInk: '#FFFFFF',
  };

  if (mode === 'dark') {
    return {
      ...brand,
      link: '#4CA875',
      // DNKADM-0297 surface ladder — the approved exploration's hue-155,
      // low-chroma OKLCH steps, assigned by each token's position in the
      // pre-0297 lightness ordering (bg < surface < surface2 < raise <
      // lineFaint < lineSoft < line < lineStrong < textGhost < text3 <
      // textMuted < textSubtle < text2 < text). The three text steps above
      // L 0.55 are derived at the same hue/chroma (the dark-only exploration
      // stops at 0.55 — it has no primary-text swatch).
      //
      // AUTHORED IN OKLCH, SHIPPED AS sRGB HEX: MUI 9's color engine
      // (decomposeColor, used by theme.alpha() inside component styles)
      // rejects oklch() strings, so each value is the exact sRGB conversion
      // (OKLab reference math, D65) of the oklch() in its comment. To extend
      // the ladder, add a step in OKLCH and convert the same way — never
      // eyeball a hex.
      bg: '#0D100E', // oklch(0.17 0.008 155)
      surface: '#141815', // oklch(0.205 0.009 155)
      surface2: '#161B18', // oklch(0.215 0.009 155)
      raise: '#1C211D', // oklch(0.24 0.010 155)
      line: '#252A27', // oklch(0.28 0.010 155)
      lineStrong: '#656C67', // oklch(0.523 0.010 155)
      lineSoft: '#202622', // oklch(0.26 0.010 155)
      lineFaint: '#1E2320', // oklch(0.25 0.010 155)
      text: '#ECEFED', // oklch(0.95 0.005 155)
      text2: '#A6ADA8', // oklch(0.739 0.010 155)
      text3: '#7F8681', // oklch(0.612 0.010 155)
      textSubtle: '#828883', // oklch(0.62 0.010 155)
      textMuted: '#818884', // oklch(0.619 0.010 155)
      textGhost: '#656C67', // oklch(0.523 0.010 155)
      brandSoft: 'rgba(46,125,82,0.28)',
      brandSoft2: 'rgba(46,125,82,0.38)',
      brandFaint: 'rgba(46,125,82,0.14)',
      brandLine: 'rgba(46,125,82,0.5)',
      success: '#2E9D5E',
      successDark: '#2B9258',
      successSoft: 'rgba(46,157,94,0.18)',
      successSoftInk: '#7FD3A3',
      warn: '#D89A2E',
      warnDark: '#B07D3D',
      warnSoft: 'rgba(216,154,46,0.16)',
      warnSoftInk: '#E8C27A',
      warnSoftLine: 'rgba(216,154,46,0.4)',
      warnBadgeBg: 'rgba(216,154,46,0.22)',
      warnBadgeInk: '#EACB8B',
      error: '#DE684D',
      errorFill: '#C0492E',
      errorDark: '#8C2F1C',
      errorSoft: 'rgba(192,73,46,0.18)',
      errorSoftInk: '#F0A090',
      errorSoftLine: 'rgba(192,73,46,0.4)',
      info: '#29B6F6',
      infoSoft: 'rgba(139,170,150,0.16)',
      infoSoftInk: '#AECBBA',
      shadowCard: '0 1px 3px rgba(0,0,0,0.5), 0 1px 2px rgba(0,0,0,0.4)',
      shadowDialog:
        '0 11px 15px -7px rgba(0,0,0,0.5), 0 24px 38px 3px rgba(0,0,0,0.4)',
      shadowAppBar: '0 1px 2px rgba(0,0,0,0.3)',
    };
  }

  return {
    ...brand,
    link: '#2E7D52',
    // DNKADM-0297 light ladder — DERIVED from the dark-only exploration by
    // keeping its character (hue 155, low chroma, fine lightness steps):
    // each token takes the OKLCH lightness its pre-0297 value already had
    // when composited on white (e.g. `rgba(0,0,0,0.6)` ≈ L 0.51), so the
    // effective contrast ordering is unchanged while every neutral gains the
    // same green tint as dark mode. Text/lines are now opaque tinted values
    // rather than black-alpha washes. Chroma tapers toward the whites (a
    // tint reads stronger per chroma unit at high L).
    //
    // Authored in OKLCH, shipped as exact sRGB hex — same MUI constraint and
    // conversion rule as the dark block above.
    bg: PAGE_BG, // oklch(0.958 0.006 155)
    surface: '#FAFCFB', // oklch(0.99 0.003 155)
    surface2: '#F6F9F6', // oklch(0.978 0.004 155)
    raise: '#F1F4F2', // oklch(0.965 0.005 155)
    line: '#DBDFDC', // oklch(0.90 0.006 155)
    lineStrong: '#CDD3CF', // oklch(0.86 0.008 155)
    lineSoft: '#E5E9E6', // oklch(0.93 0.005 155)
    lineFaint: '#EAEEEB', // oklch(0.945 0.005 155)
    text: '#1C211D', // oklch(0.24 0.010 155)
    text2: '#626864', // oklch(0.51 0.010 155)
    text3: '#9BA09C', // oklch(0.70 0.008 155)
    textSubtle: '#6D736F', // oklch(0.55 0.010 155)
    textMuted: '#858B86', // oklch(0.63 0.010 155)
    textGhost: '#B1B5B2', // oklch(0.77 0.006 155)
    brandSoft: GREEN_TINTS.navActive,
    brandSoft2: GREEN_TINTS.chip,
    brandFaint: GREEN_TINTS.surfaceB,
    brandLine: 'rgba(46,125,82,0.3)',
    success: '#2E9D5E',
    successDark: '#1F6B43',
    successSoft: STATUS.active.bg,
    successSoftInk: STATUS.active.fg,
    warn: '#D89A2E',
    warnDark: '#8A5A12',
    warnSoft: '#FFF7E8',
    warnSoftInk: '#7A5510',
    warnSoftLine: '#F2DDB0',
    warnBadgeBg: '#FFF3CD',
    warnBadgeInk: '#6B5200',
    error: '#C0492E',
    errorFill: '#C0492E',
    errorDark: '#8C2F1C',
    errorSoft: '#FDECEA',
    errorSoftInk: '#9A4A38',
    errorSoftLine: '#F5C6BF',
    info: '#0288D1',
    infoSoft: '#F0F5F1',
    infoSoftInk: '#2E5E45',
    shadowCard: SHADOWS.card,
    shadowDialog: SHADOWS.dialog,
    shadowAppBar: '0 1px 2px rgba(0,0,0,0.04)',
  };
}

/**
 * Read the design tokens off a theme, with a safe fallback. The app always
 * supplies `theme.tokens` via `buildAdminTheme`, but a bare `createTheme()`
 * (e.g. a component test that doesn't wrap in the app theme) won't — so shared
 * primitives resolve tokens through this instead of crashing on
 * `theme.tokens.*`.
 */
export function resolveTokens(theme: Theme): AdminTokens {
  return (
    (theme as { tokens?: AdminTokens }).tokens ??
    buildTokens(theme.palette.mode === 'dark' ? 'dark' : 'light')
  );
}

/** The dark-mode neutral ink base (the dark `text` family, as an alpha base). */
const DARK_INK_BASE = '233,243,236';

/**
 * Mode-aware neutral ink at an EXACT alpha (DNKADM-0269) — the sweep's escape
 * hatch for `rgba(0,0,0,α)` literals whose exact value has no semantic token.
 * Light output is byte-identical to the swept literal (`rgba(0,0,0,α)` — the
 * 0266 identity constraint); dark swaps the base to the dark ink family at the
 * same alpha. Prefer a real token (`text/2/3`, `line*`) when the alpha matches
 * one; use this only for the odd in-between values.
 */
export function inkAlpha(theme: Theme, alpha: number): string {
  return theme.palette.mode === 'dark'
    ? `rgba(${DARK_INK_BASE},${alpha})`
    : `rgba(0,0,0,${alpha})`;
}
/** Build the mode-aware MUI theme — every color below reads from tokens. */
export function buildAdminTheme(mode: 'light' | 'dark'): Theme {
  const t = buildTokens(mode);

  return createTheme({
    tokens: t,
    palette: {
      mode,
      primary: { main: t.brand, dark: t.brandDark, contrastText: t.brandInk },
      success: {
        main: t.success,
        dark: t.successDark,
        // Dark-mode success fills are bright enough to take the page-dark ink;
        // light keeps MUI's byte-identical computed contrastText.
        ...(mode === 'dark' ? { contrastText: t.bg } : {}),
      },
      warning: { main: t.warn, dark: t.warnDark },
      error: { main: t.error, dark: t.errorDark },
      background: { default: t.bg, paper: t.surface },
      text: {
        primary: t.text,
        secondary: t.text2,
        disabled: t.text3,
      },
      divider: t.line,
    },

    // `RADII.control`; ALSO the multiplier base for every numeric
    // `sx.borderRadius` in the app — do not change it (see RADII's docblock).
    shape: { borderRadius: RADII.control },

    // DNKADM-0299 U3 — the scale re-tuned for Instrument Sans (the 13/12.5/
    // 11.5 body sizes were tuned for Roboto's metrics): body up a step,
    // headings to weight 600 with em-based negative tracking (the design's
    // vocabulary), buttons sentence-case at 600 with the uppercase-era 0.4px
    // tracking dropped (that spacing existed to make uppercase legible).
    // Dense-table cell sizes (MuiTableCell below) deliberately stay at their
    // tuned values — the roster/ledger density is the truncation risk zone.
    typography: {
      fontFamily: SANS,
      h1: { fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em' },
      h2: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em' },
      h3: { fontSize: 18, fontWeight: 600, letterSpacing: '-0.015em' },
      // h6 is the de-facto page title across the config screens; style it
      // explicitly rather than riding MUI's default now that the scale is ours.
      h6: { fontSize: 20, fontWeight: 600, letterSpacing: '-0.015em' },
      subtitle1: { fontSize: 15, fontWeight: 600, letterSpacing: '-0.01em' },
      body1: { fontSize: 14 },
      body2: { fontSize: 13, color: t.text2 },
      caption: { fontSize: 12 },
      button: {
        textTransform: 'none',
        fontWeight: 600,
        letterSpacing: 0,
        fontSize: 14,
      },
      // Reusable mono treatment (tabular feel) for numbers/IDs/balances/keys.
      mono: { fontFamily: MONO, fontSize: 13, letterSpacing: 0 },
    },

    components: {
      // Page background.
      MuiCssBaseline: {
        styleOverrides: { body: { backgroundColor: t.bg } },
      },

      MuiLink: {
        styleOverrides: {
          root: { color: t.link },
        },
      },

      // Cards / Paper: 12px radius + the handoff's layered card shadow.
      // `backgroundImage: 'none'` kills MUI's dark-mode elevation overlay so
      // paper surfaces stay token-exact (a no-op in light, which has none).
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: RADII.card },
          elevation1: { boxShadow: t.shadowCard },
        },
      },

      MuiAppBar: {
        defaultProps: { color: 'inherit', elevation: 0 },
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            borderBottom: `1px solid ${t.line}`,
            boxShadow: t.shadowAppBar,
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: { width: 248, borderRight: `1px solid ${t.line}` },
        },
      },

      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: RADII.button,
            minHeight: 38,
            paddingInline: 16,
            // v9: the removed `containedPrimary` compound-class slot moves to a
            // `variants` entry (variant: 'contained' + color: 'primary').
            variants: [
              {
                props: { variant: 'contained', color: 'primary' },
                style: { '&:hover': { backgroundColor: t.brandDark } },
              },
              {
                // DNKADM-0330: `palette.error.main` is the lifted foreground
                // role. Filled destructive controls retain the darker fill so
                // their white ink also remains AA.
                props: { variant: 'contained', color: 'error' },
                style: {
                  backgroundColor: t.errorFill,
                  color: t.brandInk,
                  '&:hover': { backgroundColor: t.errorDark },
                },
              },
            ],
          },
        },
      },

      MuiChip: {
        styleOverrides: {
          // `pill` renders identically to the old 12 at the 24px height
          // (both are a full capsule) but stays a capsule at any height.
          root: {
            height: 24,
            borderRadius: RADII.pill,
            fontSize: 12,
            fontWeight: 500,
            variants: [
              {
                props: { variant: 'filled', color: 'error' },
                style: { backgroundColor: t.errorFill, color: t.brandInk },
              },
            ],
          },
          label: { paddingInline: 10 },
        },
      },

      MuiBadge: {
        styleOverrides: {
          badge: {
            '&.MuiBadge-colorError': {
              backgroundColor: t.errorFill,
              color: t.brandInk,
            },
          },
        },
      },

      // Dense tables by default.
      MuiTable: { defaultProps: { size: 'small' } },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: 13,
            paddingTop: 9,
            paddingBottom: 9,
            borderColor: t.lineFaint,
          },
          head: {
            fontSize: 12,
            fontWeight: 500,
            color: t.text2,
            backgroundColor: t.surface2,
            borderBottomColor: t.line,
          },
        },
      },

      MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADII.control,
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: t.brand,
            },
          },
        },
      },

      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 13.5,
            paddingInline: 14,
            borderColor: t.lineStrong,
            '&.Mui-selected': {
              backgroundColor: t.brand,
              color: t.brandInk,
              '&:hover': { backgroundColor: t.brandDark },
            },
          },
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: RADII.sheet, boxShadow: t.shadowDialog },
        },
      },

      // DNKADM-0454: a Select/Menu list opened inside a dialog must render ABOVE the dialog's
      // content. MUI gives popovers and dialogs the same `modal` level, so a list's place against
      // the dialog's fields rested on mount order alone; one level up makes it explicit for
      // every select in every dialog.
      MuiPopover: {
        styleOverrides: { root: ({ theme }) => ({ zIndex: theme.zIndex.modal + 1 }) },
      },

      // Tonal alerts for empty / permission / error states.
      MuiAlert: {
        styleOverrides: {
          // v9: the removed `standard{Error,Warning,Success,Info}` compound-class
          // slots move to `variants` entries (variant: 'standard' + each color).
          root: {
            borderRadius: RADII.row,
            fontSize: 13,
            alignItems: 'flex-start',
            variants: [
              {
                props: { variant: 'standard', severity: 'error' },
                style: {
                  backgroundColor: t.errorSoft,
                  color: t.errorSoftInk,
                  border: `1px solid ${t.errorSoftLine}`,
                },
              },
              {
                props: { variant: 'standard', severity: 'warning' },
                style: {
                  backgroundColor: t.warnSoft,
                  color: t.warnSoftInk,
                  border: `1px solid ${t.warnSoftLine}`,
                },
              },
              {
                props: { variant: 'standard', severity: 'success' },
                style: {
                  backgroundColor: t.successSoft,
                  color: t.successSoftInk,
                },
              },
              {
                props: { variant: 'standard', severity: 'info' },
                style: { backgroundColor: t.infoSoft, color: t.infoSoftInk },
              },
            ],
          },
        },
      },

      MuiTab: {
        styleOverrides: {
          // Sentence case with the uppercase-era tracking dropped, matching
          // the button treatment (DNKADM-0299 U3, the approved design).
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 14,
            letterSpacing: 0,
            minHeight: 46,
          },
        },
      },

      MuiSkeleton: { defaultProps: { animation: 'pulse' } },
    },
  });
}

/**
 * The static light theme — `buildAdminTheme('light')`, byte-identical to the
 * pre-0266 static export. Kept so existing `import { theme } from '@/theme'`
 * call sites (tests, non-mode-aware wrappers) keep working; the app itself
 * mounts the mode-aware theme via `AdminThemeProvider`.
 */
export const theme = buildAdminTheme('light');
